/**
 * 챗봇 → 화면 엑셀 업로드 팝업으로 표 넘기기 (aibot-v2-attachments 4단계, 2026-10-28).
 * 챗봇이 표(첫 줄 머리글)를 맡겨 두고 화면의 onExcelUpload 를 부르면, 팝업 시트(SnSheet)가 처음 초기화(resetGridData)될 때 가져간다.
 * 그래서 화면 코드는 손대지 않는다. 가져간 뒤의 검증 · 저장은 팝업 그대로이고 저장은 사용자가 누른다.
 */

export type ExcelUploadFill = {
  /** 넣은 행 수 */
  applied: number;
  /** 머리글이 맞은 칸 이름 */
  matched: string[];
  /** 맞는 머리글이 없어 붙여넣기처럼 왼쪽부터 순서대로 넣었다 */
  positional: boolean;
};

type Offer = { table: string[][]; at: number; done: (fill: ExcelUploadFill | null) => void };

/** 팝업이 이 안에 열리지 않으면 버린다 — 나중에 다른 시트가 엉뚱하게 가져가지 않게 */
const OFFER_TTL_MS = 15_000;
let current: Offer | null = null;

/** 표를 맡긴다. 시트가 가져가면 넣은 결과로, 버려지면 null 로 끝난다 */
export function offerExcelUploadTable(table: string[][]): Promise<ExcelUploadFill | null> {
  current?.done(null);
  return new Promise((resolve) => {
    const offer: Offer = { table, at: Date.now(), done: resolve };
    current = offer;
    window.setTimeout(() => {
      if (current === offer) {
        current = null;
        resolve(null);
      }
    }, OFFER_TTL_MS);
  });
}

/** 아직 아무 시트도 안 가져갔으면 거두고 true */
export function cancelExcelUploadOffer(): boolean {
  if (!current) return false;
  const offer = current;
  current = null;
  offer.done(null);
  return true;
}

/** 시트가 초기화될 때 부른다. 맡긴 표가 있으면 꺼내 간다(한 번만) */
export function takeExcelUploadTable(): Offer | null {
  const offer = current;
  current = null;
  return offer && Date.now() - offer.at < OFFER_TTL_MS ? offer : null;
}

const headerKey = (s: string) => s.replace(/[\s*]/g, "").toLowerCase();

/**
 * 표를 시트 칸 순서로 바꾼다. 첫 줄 머리글을 칸 제목(또는 칸 id)과 맞추고, 하나도 안 맞으면
 * 붙여넣기처럼 첫 줄부터 왼쪽 칸에 순서대로 넣는다. 빈 줄은 뺀다
 */
export function mapExcelUploadTable(
  table: string[][],
  columns: { field: string; header: string }[]
): { rows: string[][]; matched: string[]; positional: boolean } {
  const head = (table[0] ?? []).map((h) => headerKey(String(h ?? "")));
  const source = columns.map((c) => {
    const keys = [headerKey(c.header), headerKey(c.field)].filter(Boolean);
    return head.findIndex((h) => h !== "" && keys.includes(h));
  });
  const matched = columns.filter((_, j) => source[j] >= 0).map((c) => c.header || c.field);
  const positional = matched.length === 0;
  const rows = (positional ? table : table.slice(1))
    .map((r) => columns.map((_, j) => String((positional ? r[j] : source[j] >= 0 ? r[source[j]] : "") ?? "")))
    .filter((r) => r.some((v) => v.trim() !== ""));
  return { rows, matched, positional };
}
